<template>
  <div class="notification-list">
    <!-- Stats -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-value">{{ stats.total ?? '—' }}</div>
        <div class="stat-label">{{ $t('bo.notifications.statTotal') }}</div>
      </div>
      <div class="stat-card stat-card-unread">
        <div class="stat-value">{{ stats.unread ?? '—' }}</div>
        <div class="stat-label">{{ $t('bo.notifications.statUnread') }}</div>
      </div>
      <div v-for="(count, cat) in (stats.by_category || {})" :key="cat" class="stat-card">
        <div class="stat-value">{{ count }}</div>
        <div class="stat-label">{{ cat }}</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters">
      <select v-model="filterStatus" @change="onFilterChange" class="filter-select">
        <option value="">{{ $t('bo.notifications.allStatuses') }}</option>
        <option value="unread">{{ $t('bo.notifications.statusUnread') }}</option>
        <option value="read">{{ $t('bo.notifications.statusRead') }}</option>
      </select>
      <select v-model="filterCategory" @change="onFilterChange" class="filter-select">
        <option value="">{{ $t('bo.notifications.allCategories') }}</option>
        <option value="account">Account</option>
        <option value="mentoring_request">Mentoring Request</option>
        <option value="program">Program</option>
        <option value="session">Session</option>
        <option value="journey">Journey</option>
        <option value="feedback">Feedback</option>
        <option value="message">Message</option>
        <option value="sponsorship">Sponsorship</option>
        <option value="training">Training</option>
        <option value="billing">Billing</option>
        <option value="security">Security</option>
      </select>
      <select v-model="filterType" @change="onFilterChange" class="filter-select">
        <option value="">{{ $t('bo.notifications.allTypes') }}</option>
        <option v-for="t in availableTypes" :key="t.type" :value="t.type">{{ t.type }}</option>
      </select>
      <input
        v-model="filterUserId"
        @change="onFilterChange"
        type="number"
        :placeholder="$t('bo.notifications.filterByUserId')"
        class="filter-input"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>

    <!-- Error -->
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <!-- Empty -->
    <div v-else-if="notifications.length === 0" class="empty">
      {{ $t('bo.notifications.noNotifications') }}
    </div>

    <!-- Table -->
    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.notifications.colRecipient') }}</th>
          <th>{{ $t('bo.notifications.colCategory') }}</th>
          <th>{{ $t('bo.notifications.colType') }}</th>
          <th>{{ $t('bo.notifications.colTitle') }}</th>
          <th>{{ $t('bo.notifications.colStatus') }}</th>
          <th>{{ $t('bo.notifications.colPriority') }}</th>
          <th>{{ $t('bo.notifications.colDate') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="notif in notifications" :key="notif.id">
          <td class="col-recipient">
            <span class="user-id">#{{ notif.recipient_user_id ?? notif.notifiable_id }}</span>
          </td>
          <td>
            <span class="chip chip-gray">{{ notif.category || '—' }}</span>
          </td>
          <td class="col-type">
            <span class="type-label">{{ notif.type }}</span>
          </td>
          <td class="col-title">
            <div class="notif-title">{{ notif.title || extractTitle(notif) }}</div>
            <div v-if="notif.message" class="notif-message">{{ truncate(notif.message, 60) }}</div>
          </td>
          <td>
            <span :class="['badge', `badge-${notif.status}`]">
              {{ $t(`bo.notifications.status${capitalize(notif.status)}`) }}
            </span>
          </td>
          <td>
            <span :class="['badge-priority', `priority-${notif.priority}`]">
              {{ notif.priority || 'normal' }}
            </span>
          </td>
          <td class="col-date">{{ formatDate(notif.created_at) }}</td>
          <td class="actions">
            <a
              v-if="notif.action_url && mentorAppUrl"
              :href="mentorAppUrl + notif.action_url"
              target="_blank"
              rel="noopener"
              class="btn-action btn-link"
            >{{ $t('bo.notifications.openLink') }}</a>
            <span v-else-if="notif.action_url" class="action-url" :title="notif.action_url">
              {{ notif.action_url }}
            </span>
            <button
              @click="$emit('delete', notif.id)"
              class="btn-action btn-delete"
              :disabled="deletingId === notif.id"
            >{{ $t('bo.common.delete') }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div v-if="pagination && pagination.last_page > 1" class="pagination">
      <button
        :disabled="pagination.current_page === 1"
        @click="changePage(pagination.current_page - 1)"
        class="page-btn"
      >&larr;</button>
      <span class="page-info">{{ pagination.current_page }} / {{ pagination.last_page }} ({{ pagination.total }} {{ $t('bo.notifications.total') }})</span>
      <button
        :disabled="pagination.current_page === pagination.last_page"
        @click="changePage(pagination.current_page + 1)"
        class="page-btn"
      >&rarr;</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import notificationAdminService from '@/services/notificationAdminService'

const { t } = useI18n()

const emit = defineEmits(['delete'])

const mentorAppUrl = import.meta.env.VITE_MENTOR_APP_URL || ''

const props = defineProps({
  deletingId: { type: String, default: null },
})

const notifications = ref([])
const stats = ref({})
const availableTypes = ref([])
const loading = ref(false)
const error = ref('')
const filterStatus = ref('')
const filterCategory = ref('')
const filterType = ref('')
const filterUserId = ref('')
const pagination = ref(null)
const currentPage = ref(1)

const loadNotifications = async () => {
  loading.value = true
  error.value = ''
  try {
    const params = { page: currentPage.value, per_page: 50 }
    if (filterStatus.value) params.status = filterStatus.value
    if (filterCategory.value) params.category = filterCategory.value
    if (filterType.value) params.type = filterType.value
    if (filterUserId.value) params.user_id = filterUserId.value
    const res = await notificationAdminService.getAll(params)
    notifications.value = res.data?.data || []
    pagination.value = res.data?.meta || null
  } catch (e) {
    error.value = t('bo.notifications.failedLoad')
  } finally {
    loading.value = false
  }
}

const loadStats = async () => {
  try {
    const res = await notificationAdminService.getStats()
    stats.value = res.data?.data || {}
  } catch {
    // stats non critiques
  }
}

const loadTypes = async () => {
  try {
    const res = await notificationAdminService.getTypes()
    availableTypes.value = res.data?.data || []
  } catch {
    // types non critiques
  }
}

const onFilterChange = () => {
  currentPage.value = 1
  loadNotifications()
}

const changePage = (page) => {
  currentPage.value = page
  loadNotifications()
}

const truncate = (str, len) => str?.length > len ? str.substring(0, len) + '…' : str

const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : ''

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const extractTitle = (notif) => {
  if (notif.data && typeof notif.data === 'object') {
    return notif.data.title || notif.data.message || notif.type
  }
  return notif.type
}

defineExpose({ loadNotifications, loadStats })

onMounted(() => {
  loadNotifications()
  loadStats()
  loadTypes()
})
</script>

<style scoped>
.notification-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* Stats */
.stats-row {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.stat-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem 1.25rem;
  min-width: 100px;
  text-align: center;
}

.stat-card-unread {
  background: #fef3c7;
  border-color: #f59e0b;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}

.stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.2rem;
  text-transform: capitalize;
}

/* Filters */
.filters {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filter-select,
.filter-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.filter-input {
  width: 130px;
}

.filter-input::placeholder {
  color: #9ca3af;
}

/* States */
.loading,
.empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
}

/* Table */
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.table tr:last-child td {
  border-bottom: none;
}

.col-recipient {
  white-space: nowrap;
}

.user-id {
  font-size: 0.8rem;
  color: #6b7280;
  font-family: monospace;
}

.col-type {
  max-width: 160px;
}

.type-label {
  font-size: 0.775rem;
  color: #374151;
  font-family: monospace;
  background: #f3f4f6;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
}

.col-title {
  max-width: 220px;
}

.notif-title {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
}

.notif-message {
  color: #6b7280;
  font-size: 0.775rem;
  margin-top: 0.15rem;
}

.col-date {
  white-space: nowrap;
  font-size: 0.775rem;
  color: #6b7280;
}

/* Chips */
.chip {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.chip-gray {
  background: #f3f4f6;
  color: #374151;
}

/* Badges */
.badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-unread {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-read {
  background: #f3f4f6;
  color: #6b7280;
}

.badge-priority {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
}

.priority-high {
  color: #d97706;
}

.priority-critical {
  color: #dc2626;
  font-weight: 700;
}

/* Actions */
.actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.action-url {
  font-size: 0.7rem;
  font-family: monospace;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  max-width: 140px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.btn-action {
  padding: 0.3rem 0.6rem;
  border-radius: 0.375rem;
  font-size: 0.775rem;
  cursor: pointer;
  border: 1px solid;
  font-weight: 500;
  transition: all 0.15s;
  white-space: nowrap;
  text-decoration: none;
  display: inline-block;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-link {
  border-color: #3b82f6;
  color: #3b82f6;
  background: white;
}

.btn-link:hover {
  background: #eff6ff;
}

.btn-delete {
  border-color: #ef4444;
  color: #ef4444;
  background: white;
}

.btn-delete:hover:not(:disabled) {
  background: #fef2f2;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-btn:hover:not(:disabled) {
  background: #f9fafb;
}

.page-info {
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
